import "@/types/Object.extensions";
import { useProjectData } from "@api/analysis";
import { ModelData, ScenarioData } from "@api/apiClient";
import { GridToolbarCustom } from "@components/DataGrid/components/toolbar/GridToolbarCustom";
import DataGrid from "@components/DataGrid/DataGrid";
import { sumWithNullsAgg } from "@components/DataGrid/utils/aggregation";
import useRowSelectionModel from "@features/analysis/hooks/useSelectionModel";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { useTheme } from "@mui/material";
import {
  GRID_AGGREGATION_FUNCTIONS,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridGroupNode,
  GridRowId,
  GridRowSelectionModel,
  GridRowSelectionPropagation,
} from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import _ from "lodash";
import { forwardRef, MutableRefObject, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getColumns, getFilteredColumns, getGroupingColumnDef } from "./ResultsGrid.columns";

export interface ResultGridProps {
  apiRef: MutableRefObject<GridApiPremium>;
  onSelect: (ids: string[]) => void;
}

const gridStorageKey = "results-grid";

const defaultInitialState: GridInitialStatePremium = {
  density: "compact",
  aggregation: {
    model: {
      a1A2A3: "sum",
      a4A5: "sum",
      a: "sum",
      b1: "sum",
      b2B5: "sum",
      biogenic: "sum",
      b: "sum",
      c: "sum",
      d: "sum",
      totalCo2: "sum",
      percentRebar: "avg",
      percentAssigned: "avg",
    },
  },
  pinnedColumns: {
    left: [GRID_CHECKBOX_SELECTION_FIELD, GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, GRID_TREE_DATA_GROUPING_FIELD],
  },
};

const rowSelectionPropagation: GridRowSelectionPropagation = {
  parents: true,
  descendants: true,
};

const slots = {
  toolbar: GridToolbarCustom,
};

const slotProps = {
  toolbar: {
    showQuickFilter: false,
    showSearchFilter: false,
    printOptions: { disableToolbarButton: true },
    csvOptions: { disableToolbarButton: true },
    excelOptions: { disableToolbarButton: true },
    headerFiltersOptions: { disableToolbarButton: true },
  },
};

const getRowId = (row: any) => {
  try {
    return row.key;
  } catch (error) {
    console.error("getRowId error: ", error);
  }
};

const getTreeDataPath = (row: any) => {
  return row.path;
};

const ResultsGridRef = forwardRef<any, PropsWithChildren<ResultGridProps>>((props, ref) => {
  const { apiRef } = props;
  const appStore = useAppStore();
  const theme = useTheme();
  const { id: projectId } = useParams();
  const [key, setKey] = useState(0); // increment key to force re-initialize of the grid
  const projectData = useProjectData(projectId!);

  const { handelRowSelectionModelChange, selectionModel } = useRowSelectionModel(apiRef);

  const handleRowSelectionChange = async (selection: GridRowSelectionModel) => {
    console.log("onSelectionChange before: ", selection);

    // GridRowSelectionModel is readonly so we need to convert it to a mutable array
    const { models, newRowSelectionModel } = await handelRowSelectionModelChange(selection as GridRowId[]);

    console.log("onSelectionChange after: ", selection);

    _.delay(async () => {
      for (const item of models) {
        const identifier = item.data.model?.apsViewId || item.data.model?.modelId;
        const isViewerModel = !!item.data.model?.apsViewId;
        await appStore.selectionManager.selectModels(item.data.scenarioId, identifier, true, isViewerModel);
      }
      props.onSelect(newRowSelectionModel as string[]);
    }, 100);
  };

  useEffect(() => {
    const newRows: any = [];

    projectData.data?.scenarioDatas?.forEach((scenarioData: ScenarioData, index: number) => {
      let percentOfEmissionReductionTarget = ((scenarioData.co2Aggregates?.totalCo2 || 0) / (appStore.project?.projectEmissionReductionTarget || 0)) * 100;
      percentOfEmissionReductionTarget = _.isFinite(percentOfEmissionReductionTarget) ? percentOfEmissionReductionTarget : 0;
      const scenario = {
        index,
        key: scenarioData.scenarioId,
        path: [scenarioData.scenarioId],
        type: "scenario",
        data: {
          ...scenarioData,
          tag: `S${index + 1}`,
          percentOfEmissionReductionTarget,
          percentAssigned: scenarioData.getPercentageAssigned(),
        },
      };
      newRows.push(scenario);

      scenarioData.modelDatas?.forEach((modelData: ModelData, modelIndex: number) => {
        percentOfEmissionReductionTarget = ((modelData.co2Aggregates?.totalCo2 || 0) / (modelData.model?.modelEmissionReductionTarget || 0)) * 100;
        percentOfEmissionReductionTarget = _.isFinite(percentOfEmissionReductionTarget) ? percentOfEmissionReductionTarget : 0;
        const model = {
          index: modelIndex,
          key: modelData.scenarioModelId,
          path: [scenarioData.scenarioId, modelData.scenarioModelId],
          type: "model",
          data: {
            ...modelData,
            tag: `S${index + 1}-M${modelIndex + 1}`,
            name: modelData.model?.modelName,
            totalCo2: modelData.co2Aggregates?.totalCo2, // TODO: must subtract the totalCo2 of the rebar and non-modelled elements
            percentOfEmissionReductionTarget: undefined, //percentOfEmissionReductionTarget,
            percentAssigned: modelData.percentageAssign,
          },
        };
        newRows.push(model);
      });
    });

    console.log("rows: ", newRows);

    apiRef.current?.updateRows?.(newRows || []);
  }, [apiRef.current?.updateRows, projectData.data]);

  const groupingColumnDef = useMemo(() => getGroupingColumnDef(i18n.t("results-grid.columns.grouping")), [apiRef]);

  const columns = useMemo(() => getColumns(apiRef, appStore, theme), [apiRef, appStore, theme]);
  const { filteredColumns, columnVisibilityModel: initialVisibilityModel } = useMemo(() => getFilteredColumns(columns, appStore), [columns, appStore]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(initialVisibilityModel);

  const initialState = useMemo(
    () => ({
      ...defaultInitialState,
      columns: {
        columnVisibilityModel,
      },
      headerFilters: true, // this needs to be sync'd with DataGridContext.headerFilters
    }),
    [columnVisibilityModel],
  );

  const getAggregatePosition = useCallback((groupNode: GridGroupNode) => {
    return groupNode.depth === -1 ? null : "inline";
  }, []);

  const aggregationFunctions = useMemo(() => {
    return {
      ...GRID_AGGREGATION_FUNCTIONS,
      sum: sumWithNullsAgg,
    };
  }, []);

  console.log(initialState);
  console.log(filteredColumns);
  console.log(apiRef.current?.state?.rows);

  // TODO: disable header selection behavior
  return (
    <DataGrid
      key={key}
      ref={ref}
      apiRef={apiRef}
      treeData
      autosizeColumns
      autosizeTreeDataGroupingColumns
      checkboxSelection
      disableRowSelectionOnClick
      getTreeDataPath={getTreeDataPath}
      columns={filteredColumns}
      rowSelectionPropagation={rowSelectionPropagation}
      initialState={initialState}
      groupingColDef={groupingColumnDef}
      getRowId={getRowId}
      slots={slots}
      slotProps={slotProps}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={handleRowSelectionChange}
      loading={projectData.isFetching}
      aggregationFunctions={aggregationFunctions}
      getAggregationPosition={getAggregatePosition}
      hideFooter
      //disableDensitySelector
      disableColumnFilter
      //disableColumnMenu
      //disableColumnSorting
      //disableMultipleColumnsSorting
      //disableColumnReorder
      //disableColumnPinning
      storageKey={gridStorageKey}
    />
  );
});

const ResultsGrid = ResultsGridRef;
export default ResultsGrid;
